import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  CalendarX2,
  ChevronRight,
  Clock,
} from "lucide-react";
import {
  fetchBlogPosts,
  fetchEvents,
  type BlogPost,
  type CmsEvent,
} from "../../lib/api";

type Load<T> =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: T[] };

function useCmsList<T>(loader: (signal: AbortSignal) => Promise<T[]>) {
  const [state, setState] = useState<Load<T>>({ status: "loading" });
  useEffect(() => {
    const ctrl = new AbortController();
    loader(ctrl.signal)
      .then(data => setState({ status: "ready", data }))
      .catch((err: Error) => {
        if (!ctrl.signal.aborted) {
          setState({ status: "error", message: err.message });
        }
      });
    return () => ctrl.abort();
  }, [loader]);
  return state;
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

function fmtTime(t?: string) {
  if (!t) {
    return null;
  }
  const [h, m] = t.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m || 0);
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

// Articles without a cover use the palm-leaf manuscript from the approved section artwork.
const COVER_FALLBACK = "/images/home/insight-manuscript.webp";

function EventCard({ event }: { event: CmsEvent }) {
  const iso = event.start_date ?? event.date;
  const d = iso ? new Date(iso) : null;
  const start = fmtTime(event.start_time);
  const end = fmtTime(event.end_time);
  return (
    <Link to={`/community/events/${event.id}`} className="ev-card">
      <span className="ev-date">
        <span className="ev-month">
          {d ? d.toLocaleDateString("en-US", { month: "short" }) : "TBA"}
        </span>
        <span className="ev-day">{d ? d.getDate() : "–"}</span>
      </span>
      <span className="ev-body">
        <span className="ev-title">{event.title}</span>
        {start && (
          <span className="ev-meta">
            <Clock aria-hidden="true" />
            {start}
            {end && ` – ${end}`}
          </span>
        )}
      </span>
      <ChevronRight className="ev-chevron" aria-hidden="true" />
    </Link>
  );
}

export function EventsInsights() {
  const events = useCmsList<CmsEvent>(fetchEvents);
  const posts = useCmsList<BlogPost>(fetchBlogPosts);

  const upcoming =
    events.status === "ready"
      ? events.data
          .filter(e => {
            const iso = e.start_date ?? e.date;
            return !iso || new Date(iso) >= new Date(new Date().toDateString());
          })
          .slice(0, 2)
      : [];
  const latestPost = posts.status === "ready" ? posts.data[0] : undefined;

  return (
    <section className="feeds" aria-label="Upcoming events and latest insights">
      <div className="feeds-grid container">
        <div className="feed-col">
          <div className="feed-head">
            <CalendarDays
              className="feed-head-icon"
              aria-hidden="true"
              strokeWidth={1.3}
            />
            <div>
              <h2 className="feed-title">Upcoming Events</h2>
              <p className="feed-sub">Lectures, workshops, and gatherings.</p>
            </div>
          </div>

          <div className="feed-body-area">
            {events.status === "loading" && (
              <div className="feed-skeleton" aria-hidden="true" />
            )}
            {upcoming.length > 0 && (
              <div className="ev-list">
                {upcoming.map(e => (
                  <EventCard key={e.id} event={e} />
                ))}
              </div>
            )}
            {(events.status === "error" ||
              (events.status === "ready" && upcoming.length === 0)) && (
              <div className="ev-empty">
                <img
                  className="ev-empty-art"
                  src="/images/home/event-temple.webp"
                  alt=""
                  loading="lazy"
                  width={220}
                  height={178}
                />
                <span className="ev-empty-icon" aria-hidden="true">
                  <CalendarX2 strokeWidth={1.6} />
                </span>
                <p className="ev-empty-title">
                  {events.status === "error"
                    ? "Events could not be loaded"
                    : "No upcoming events scheduled"}
                </p>
                <p className="ev-empty-note">
                  {events.status === "error"
                    ? "Open the events page to try again."
                    : "New lectures and gatherings will be listed here."}
                </p>
              </div>
            )}
          </div>

          <Link to="/community/events" className="btn btn-outline feed-btn">
            View All Events
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>

        <span className="feeds-divider" aria-hidden="true" />

        <div className="feed-col">
          <div className="feed-head">
            <BookOpen
              className="feed-head-icon"
              aria-hidden="true"
              strokeWidth={1.3}
            />
            <div>
              <h2 className="feed-title">Latest Insights</h2>
              <p className="feed-sub">Articles, research updates, and news.</p>
            </div>
          </div>

          <div className="feed-body-area">
            {posts.status === "loading" && (
              <div className="feed-skeleton" aria-hidden="true" />
            )}
            {latestPost && (
              <Link
                to={`/community/blog/${latestPost.id}`}
                className="art-card"
              >
                <span className="art-cover">
                  <img
                    src={latestPost.cover_image ?? COVER_FALLBACK}
                    alt=""
                    loading="lazy"
                  />
                </span>
                <span className="art-body">
                  <span className="art-kicker">
                    <span className="tag">Blog</span>
                    <time dateTime={latestPost.created_at}>
                      {fmtDate(latestPost.created_at)}
                    </time>
                  </span>
                  <span className="art-title">{latestPost.title}</span>
                  <span className="art-excerpt">{latestPost.excerpt}</span>
                </span>
                <ChevronRight className="ev-chevron" aria-hidden="true" />
              </Link>
            )}
            {(posts.status === "error" ||
              (posts.status === "ready" && !latestPost)) && (
              <div className="ev-empty">
                <p className="ev-empty-title">
                  {posts.status === "error"
                    ? "Articles could not be loaded"
                    : "No articles published yet"}
                </p>
                <p className="ev-empty-note">
                  Visit the blog for research updates and news.
                </p>
              </div>
            )}
          </div>

          <Link to="/community/blog" className="btn btn-outline feed-btn">
            View All Insights
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
